import { getProxyLink, getSiteUrl } from "@repo/shared"
import React from "react"

import { MarketsInfo } from "../../../servers/spotoverseascenter/interface"
import { NavigationItem } from "../../MainNavigation/type"
import { TranslateFunction } from "../types"

/**
 * 判断是否为测试环境
 */
export function isTest(): string {
  const env = process.env.NEXT_PUBLIC_ENV
  return env !== "production" ? "test" : ""
}

/**
 * 生成 Database 菜单项的标题（包含 Pro 标签）
 */
function createDatabaseTitle(t: TranslateFunction): React.ReactNode {
  return (
    <div key="database-pro-title">
      {t("menu.Database")}
      <sup
        style={{
          color: "#D7000F",
          fontSize: 8,
          marginLeft: 1,
        }}
      >
        {t("menu.DatabasePro")}
      </sup>
    </div>
  )
}

/**
 * 生成 Markets 菜单
 */
function generateMarketsMenu(markets: MarketsInfo[] | undefined, locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.Markets"),
    href: "#",
    mobileMenu: {
      mode: "drilldown",
      leadingLink: {
        title: t("utilityNav.MyWatchlist"),
        href: getSiteUrl("www") + `/${locale}/watchlist`,
      },
    },
    children:
      markets?.map((item) => ({
        title: item.top_name,
        href: "#",
        isHot: item.is_hot,
        children: item.next_list.map((child) => {
          return {
            title: child.second_name,
            href: getSiteUrl("www") + `/${locale}/${child.linked_item}`,
          }
        }),
      })) || [],
  }
}

/**
 * 生成 DataCenter 菜单
 */
function generateDataCenterMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.DataCenter"),
    href: "#",
    mobileMenu: {
      mode: "drilldown",
    },
    children: [
      {
        title: t("menu.PriceCenter"),
        href: getSiteUrl("www") + getProxyLink({ locale, path: "", appName: "price-center" }),
      },
      {
        title: createDatabaseTitle(t),
        href: ``,
        children: [
          {
            title: t("menu.Web"),
            href: `https://${isTest()}data-pro.metal.com/`,
            openInNewTab: true,
          },
          {
            title: t("menu.ExcelAddIn"),
            href: getSiteUrl("www") + `/${locale}/excel-add-in`,
            openInNewTab: true,
          },
          {
            title: t("menu.API"),
            href: getSiteUrl("www") + `/${locale}/smm-api`,
            openInNewTab: true,
          },
        ],
      },
    ],
  }
}

/**
 * 生成 NewsAndReports 菜单
 */
function generateNewsAndReportsMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.NewsAndReports"),
    href: "#",
    children: [
      {
        title: t("menu.News"),
        href: getSiteUrl("news") + `/${locale}`,
      },
      {
        title: t("menu.Reports"),
        href: getSiteUrl("reports") + `/${locale}`,
      },
    ],
  }
}

/**
 * 生成 Events 菜单
 */
function generateEventsMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.Events"),
    href: "#",
    children: [
      {
        title: t("menu.EventsAndFieldTrip"),
        href: getSiteUrl("www") + `/${locale}/events`,
      },
      {
        title: t("menu.Webinars"),
        href: getSiteUrl("www") + getProxyLink({ locale, path: "", appName: "webinar" }),
      },
    ],
  }
}

/**
 * 生成 Methodology 菜单
 */
function generateMethodologyMenu(locale: string, t: TranslateFunction): NavigationItem {
  return {
    title: t("menu.Methodology"),
    href: "#",
    children: [
      {
        title: t("menu.Methodology"),
        href: getSiteUrl("www") + `/${locale}/methodology`,
      },
      {
        title: t("menu.HolidayPricingSchedule"),
        href: getSiteUrl("www") + `/${locale}/holiday-pricing-schedule`,
      },
      {
        title: t("menu.ComplianceCenter"),
        href: getSiteUrl("www") + `/${locale}/compliance-center/compliance-statement`,
      },
    ],
  }
}

/**
 * 生成菜单数据（主函数）
 */
export function generateMenuData(
  markets: MarketsInfo[] | undefined,
  locale: string,
  t: TranslateFunction
): NavigationItem[] {
  const industryLink = (path: string) =>
    getSiteUrl("www") +
    getProxyLink({
      locale,
      path,
      appName: "industry",
    })

  return [
    generateMarketsMenu(markets, locale, t),
    {
      title: t("menu.Industry"),
      href: "#",
      mobileMenu: {
        mode: "drilldown",
      },
      children: [
        {
          title: t("menu.Car"),
          href: getSiteUrl("car"),
          openInNewTab: true,
        },
        {
          title: t("menu.PreciousMetals"),
          href: industryLink("precious-metals"),
          pcHide: true,
          children: [
            {
              title: t("menu.PreciousHome"),
              href: industryLink("precious-metals/home"),
            },
            {
              title: t("menu.PreciousGold"),
              href: industryLink("precious-metals/gold"),
            },
            {
              title: t("menu.PreciousSilver"),
              href: industryLink("precious-metals/silver"),
            },
            {
              title: t("menu.PreciousPGMs"),
              href: industryLink("precious-metals/pgms"),
            },
          ],
        },
      ],
    },
    generateDataCenterMenu(locale, t),
    generateNewsAndReportsMenu(locale, t),
    generateEventsMenu(locale, t),
    generateMethodologyMenu(locale, t),
  ]
}
